import { useEffect, useRef, type ReactNode } from "react";
import { csvFilename, downloadCsv, toCsv } from "../lib/csv";

/* A record opens over the list rather than replacing it.
 *
 * docs/screens.md: closing returns you to your place and your scroll position
 * rather than to the top of a re-fetched table. The list stays mounted behind
 * it, which is the whole point.
 *
 * Escape closes it, focus moves into it on open and back to the page on close,
 * and the heading is announced, because docs/design-standards.md treats all of
 * that as a build requirement rather than a pass at the end. */
export function Panel({
  title,
  subtitle,
  onClose,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      // Back where they were, so somebody using a keyboard is not dropped at
      // the top of the document every time they close a record.
      previous?.focus?.();
    };
  }, [onClose]);

  return (
    <>
      <div className="panel-scrim" onClick={onClose} aria-hidden="true" />
      <aside
        className="panel"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Record"}
        tabIndex={-1}
        ref={ref}
      >
        <header className="panel-head">
          <div>
            <h2>{title}</h2>
            {subtitle ? <p className="muted" style={{ margin: ".15rem 0 0" }}>{subtitle}</p> : null}
          </div>
          <button onClick={onClose} aria-label="Close this record">Close</button>
        </header>
        <div className="panel-body">{children}</div>
      </aside>
    </>
  );
}

/** A labelled value. Used enough times across three records to be shared. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <div>{children}</div>
    </div>
  );
}

/** A section of a record, with its own heading so the panel reads in order. */
export function Section({ title, count, download, children }: {
  title: string;
  count?: number | null;
  /* Anything on screen can be taken away as a CSV, and that includes the tables
     inside a record rather than only the big lists. These sets are small and
     already fully loaded, because every one of these queries has a limit on it,
     so the download is of exactly what is shown with no second request and no
     row ceiling to explain. */
  download?: { name: string; columns: string[]; rows: readonly unknown[] };
  children: ReactNode;
}) {
  return (
    <section className="panel-section">
      <h3>
        {title}
        {count === null || count === undefined ? null : (
          <span className="muted" style={{ fontWeight: 400 }}> ({count.toLocaleString()})</span>
        )}
        {download && download.rows.length ? (
          <button
            className="row-action"
            style={{ marginLeft: "0.6rem", fontWeight: 400 }}
            onClick={() => downloadCsv(
              csvFilename(download.name),
              toCsv(download.columns, download.rows as Record<string, unknown>[]),
            )}
          >
            CSV
          </button>
        ) : null}
      </h3>
      {children}
    </section>
  );
}
